import type { HTMLAttributes, JSX, ReactNode } from 'react';
import { cn } from '@lib/cn';

/** Native HTML elements allowed as the root tag of a `<Container>`. */
export type ContainerElement = 'div' | 'section' | 'main' | 'header' | 'footer';

interface ContainerProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  as?: ContainerElement;
  className?: string;
  children?: ReactNode;
}

/**
 * Fixed classes applied to EVERY Container. Vertical padding is intentionally
 * absent — each section owns its own vertical rhythm via the `className` prop
 * on its `<section>` wrapper. The Container only constrains width and pads
 * horizontally (clean-code §8.O: open for extension via `cn()` merge).
 */
const CONTAINER_CLASSES = 'mx-auto w-full max-w-7xl px-6 lg:px-8';

/**
 * Layout primitive: max-width 1280px (`max-w-7xl`) with responsive horizontal
 * padding (`px-6` mobile, `lg:px-8` desktop), horizontally centered (`mx-auto`).
 *
 * Polymorphic via the `as` prop — pass `'section'` / `'main'` / `'header'` /
 * `'footer'` when the container itself is the semantic landmark root. Defaults
 * to `'div'` so the most common case (Container nested inside an already-tagged
 * `<section>`) stays semantically silent.
 *
 * @example Inside a section wrapper that owns the spacing + decorations
 * ```tsx
 * <section id="hero" className="relative overflow-hidden bg-neutral-50">
 *   <Container>...content...</Container>
 * </section>
 * ```
 */
export const Container = ({
  as: Tag = 'div',
  className,
  children,
  ...rest
}: ContainerProps): JSX.Element => (
  <Tag className={cn(CONTAINER_CLASSES, className)} {...rest}>
    {children}
  </Tag>
);
